import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  AuthGuestDto,
  AuthGuestResponseDto,
  GuestChoiceDto,
  GuestPostPurchaseChoice,
} from "@ac-expo/api-types";
import { privateAxios, publicAxios } from "../axiosInstances";
import { guestCheckoutKeys } from "../query-keys/guest-checkout.keys";

type SetGuestChoiceInput = {
  orderId: string;
  choice: GuestPostPurchaseChoice;
};

type SendGuestInvoiceInput = {
  orderId: string;
};

const authGuest = async (body: AuthGuestDto) => {
  try {
    const { data } = await publicAxios.post<AuthGuestResponseDto>(
      "/guest-checkout/auth",
      body,
      { withCredentials: true }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useAuthGuest = () => {
  return useMutation({
    mutationFn: authGuest,
  });
};

const setGuestChoice = async (orderId: string, body: GuestChoiceDto) => {
  try {
    const { data } = await privateAxios.post(
      `/guest-checkout/orders/${orderId}/choice`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useSetGuestChoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, choice }: SetGuestChoiceInput) =>
      setGuestChoice(orderId, { choice }),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({
        queryKey: guestCheckoutKeys.status(orderId),
      });
    },
  });
};

const sendGuestInvoice = async (orderId: string) => {
  try {
    const { data } = await privateAxios.post(
      `/guest-checkout/orders/${orderId}/invoice`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useSendGuestInvoice = () => {
  return useMutation({
    mutationFn: ({ orderId }: SendGuestInvoiceInput) =>
      sendGuestInvoice(orderId),
  });
};
